import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, FileText } from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { PageHeader, SectionCard, EmptyState, TABLE_HEAD, TABLE_ROW } from "../components/common";
import { DepotField } from "../components/DepotField";
import { CartEditor, cartItems } from "../components/CartEditor";
import { CustomerFields, useFormDepot } from "./Sell";
import { useApi } from "../hooks/useApi";
import { useDepot } from "../context/DepotContext";
import { api, formatApiError, money, dateTimeFmt, daysAheadStr } from "../lib/api";

export default function Quotations() {
  const navigate = useNavigate();
  const { depotParam, scopeLabel } = useDepot();
  const [depotId, setDepotId] = useFormDepot();
  const [lines, setLines] = useState([]);
  const [customer, setCustomer] = useState("");
  const [phone, setPhone] = useState("");
  const [validUntil, setValidUntil] = useState(daysAheadStr(14));
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const products = useApi("/products", { depot_id: depotParam });
  const quotes = useApi("/quotations", { depot_id: depotParam });

  const submit = async (e) => {
    e.preventDefault();
    if (!lines.length) { toast.error("Add at least one product"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/quotations", { depot_id: depotId, customer, customer_phone: phone, valid_until: validUntil, note, items: cartItems(lines) });
      toast.success(`Quotation ${data.quote_no} created`);
      navigate(`/quotations/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader eyebrow={scopeLabel} title="Quotations" subtitle="Give customers a priced quotation on company letterhead. Stock is only deducted when you convert it to a sale." />
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
        <SectionCard title="New quotation" className="xl:col-span-7 self-start">
          <form onSubmit={submit} className="space-y-4 p-5" data-testid="quotation-form">
            <DepotField value={depotId} onChange={(v) => { setDepotId(v); setLines([]); }} testid="quotation-depot-select" />
            <CartEditor products={products.data || []} depotId={depotId} lines={lines} setLines={setLines} testid="quotation-cart" />
            <CustomerFields customer={customer} setCustomer={setCustomer} phone={phone} setPhone={setPhone} required prefix="quotation" />
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label className="label-mono">Valid until</Label>
                <Input type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} className="bg-slate-950" data-testid="quotation-valid-until-input" /></div>
              <div className="space-y-1.5"><Label className="label-mono">Note</Label>
                <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional" className="bg-slate-950" data-testid="quotation-note-input" /></div>
            </div>
            <Button type="submit" disabled={busy} className="h-11 w-full bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold" data-testid="quotation-submit-button">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />} Create quotation
            </Button>
          </form>
        </SectionCard>
        <SectionCard title="Recent quotations" className="xl:col-span-5">
          {!quotes.data?.length ? <EmptyState text="No quotations yet" testid="quotations-empty" /> : (
            <table className="w-full text-sm" data-testid="quotations-table">
              <thead className={TABLE_HEAD}><tr><th className="px-4 py-2 text-left">Quote</th><th className="px-4 py-2 text-left">Customer</th><th className="px-4 py-2 text-right">Total</th></tr></thead>
              <tbody>{quotes.data.map((q) => (
                <tr key={q.id} className={TABLE_ROW}>
                  <td className="px-4 py-2"><Link to={`/quotations/${q.id}`} className="font-mono text-amber-400 hover:underline" data-testid={`quotation-link-${q.quote_no}`}>{q.quote_no}</Link>
                    <div className="text-xs text-slate-500">{dateTimeFmt(q.created_at)} · <span className={q.status === "open" ? "text-blue-400" : "text-emerald-400"}>{q.status === "open" ? "Open" : `Sold ${q.receipt_no}`}</span></div></td>
                  <td className="px-4 py-2 text-slate-300">{q.customer}</td>
                  <td className="px-4 py-2 text-right font-mono">{money(q.total)}</td>
                </tr>
              ))}</tbody>
            </table>
          )}
        </SectionCard>
      </div>
    </div>
  );
}
